import { z } from 'zod';
import { MONEY_PATTERN } from '@miraki/money';
import { zId } from './primitives.js';
import { zPercent } from './access.js';

/**
 * Consignment — BRD v1.6 §6.9–§6.12. RESPONSE shapes carry money as decimal strings validated
 * against the money pattern (never `number`, never `zMoney`, which transforms on input).
 * Budget in use and the payable are DERIVED figures; nothing here is a stored balance.
 */
const zDecimal = z.string().regex(MONEY_PATTERN, 'must be a decimal amount');

/**
 * CN-13, CN-14 — the state of ONE piece on a return request. Mirrors `returnLineStates` in
 * `db/schema/return-request.ts`; the schema is the authority and this must not drift from it.
 */
export const zReturnLineState = z.enum([
  'REQUESTED', 'APPROVED', 'REFUSED', 'WITHDRAWN', 'RECEIVED', 'SOLD_INSTEAD',
]);
export type ReturnLineState = z.infer<typeof zReturnLineState>;

export const zPieceStatus = z.enum([
  /*
   * D77 — a piece exists from the moment it is MADE, before it is allocated to anybody. The
   * database has carried this state since 0031 and this enum did not, which meant any response
   * carrying a made-but-unshipped piece failed to parse. A retailer never sees one (it is still
   * at Miraki); an Admin view of the same contract does.
   */
  'MADE',
  'IN_TRANSIT', 'DISCREPANCY', 'HELD', 'SOLD', 'INVOICED', 'PAID',
  'RETURNED', 'LOST', 'DAMAGED', 'CHARGED', 'INSURANCE_CLAIM', 'WRITTEN_OFF',
]);
export type PieceStatus = z.infer<typeof zPieceStatus>;

export const zPiece = z.object({
  id: zId,
  jobNumber: z.string(),
  description: z.string(),
  goldColour: z.string().nullable(),
  ringSize: z.string().nullable(),
  wholesaleValue: zDecimal,
  rpValue: zDecimal,
  currency: z.string().length(3),
  status: zPieceStatus,

  /** CN-05, SL-06 — the piece's image. Null where the variant has none; never a placeholder URL. */
  imageUrl: z.string().nullable(),
  /**
   * CN-04 — the article (category) this piece belongs to, so the held list can be FILTERED by it.
   * `InventorySummary.byArticle` already gives the counts; without this field a piece cannot say
   * which chip it belongs to, so the filter could only be decorative.
   */
  article: z.string().nullable(),
  /**
   * SL-07, CT-08 — the variant this piece is, so "add it back to the cart" can resolve a product
   * to re-order. Also what SL-08's best-sellers must group by: grouping on `description` counts
   * two differently-sized variants of one ring as one product.
   */
  variantCode: z.string().nullable(),
  /**
   * SL-01, CN-14 — set when this piece sits on a live return request, so a screen does not offer
   * "Mark as sold" on a piece Admin has already approved for return. Advisory only: the server
   * refuses it regardless, because the request can be approved between the read and the write.
   */
  returnRequest: z.object({ state: zReturnLineState }).nullable(),

  receivedAt: z.string().nullable(),
  /**
   * Whole days held, in the portal's timezone (CN-07). Null until received.
   *
   * **It ends at the sale, not at today** (D80). For a sold piece this is days from receipt to
   * `soldOn` and does not keep growing, which is what makes R-21's "days to sale" and AN-02's
   * average meaningful. Nothing may recompute it from `receivedAt` and the clock.
   */
  daysHeld: z.number().int().nonnegative().nullable(),
  soldOn: z.string().nullable(),
});
export type Piece = z.infer<typeof zPiece>;

/** BG-02, BG-03, BG-10 — what makes up the figure, so a retailer can see why. */
export const zBudgetView = z.object({
  currency: z.string().length(3),
  assigned: zDecimal,
  inUse: z.object({
    openOrders: zDecimal,
    inTransit: zDecimal,
    held: zDecimal,
    /** AUS only (BG-02 d): sold but not yet paid. '0.0000' where value returns on sale. */
    soldUnpaid: zDecimal,
    total: zDecimal,
  }),
  /** assigned − inUse.total; negative is shown as '0.0000' (BG-08), the retailer cannot order. */
  available: zDecimal,
  releaseOn: z.enum(['SALE', 'PAYMENT']),
});
export type BudgetView = z.infer<typeof zBudgetView>;

/** CN-04 */
export const zInventorySummary = z.object({
  currency: z.string().length(3),
  wholesaleValue: zDecimal,
  rpValue: zDecimal,
  piecesOnHand: z.number().int().nonnegative(),
  byArticle: z.array(z.object({ article: z.string(), pieces: z.number().int().nonnegative() })),
});
export type InventorySummary = z.infer<typeof zInventorySummary>;

/**
 * SL-04 — the sale date defaults to today and may go back up to ⚙️ 7 days.
 *
 * In this package because BOTH sides need the same number: the screen so its picker does not
 * offer a date the server will refuse, and the server because a client is not a guard. It was
 * written in the web only, which meant the enforced rule and the offered rule were two
 * declarations with nothing holding them together.
 *
 * "Today" is the PORTAL's calendar day (D49), which a browser cannot know — it comes from the
 * API. Neither side may substitute the device's date.
 */
export const SALE_DATE_BACK_DAYS = 7;

export function saleDateBounds(today: string): { min: string; max: string } {
  const d = new Date(`${today}T00:00:00Z`);
  d.setUTCDate(d.getUTCDate() - SALE_DATE_BACK_DAYS);
  return { min: d.toISOString().slice(0, 10), max: today };
}

/** SL-01…SL-05 — one or several pieces, an optional earlier sale date (up to 7 days back). */
export const zMarkSold = z.object({
  pieceIds: z.array(zId).min(1).max(100),
  saleDate: z.string().regex(/^\d{4}-\d{2}-\d{2}$/).optional(),
});
export type MarkSold = z.infer<typeof zMarkSold>;

/** FN-02, FN-09 */
export const zPayableView = z.object({
  currency: z.string().length(3),
  /**
   * FN-02 — derived from the ledger, never stored. May be negative (a credit, FN-08).
   *
   * The formula is NOT restated here. It lived as prose in this file and in
   * `consignment.service.ts`, the two disagreed, and both omitted four of the
   * seven entry types FN-01 lists. It is now one tested function:
   * `apps/api/src/consignment/payable.ts`.
   */
  outstanding: zDecimal,
  overdue: zDecimal,
  nextDueOn: z.string().nullable(),
  invoices: z.array(z.object({
    id: zId, reference: z.string(), period: z.string().nullable(),
    amount: zDecimal, paid: zDecimal,
    /** FN-04's issue date. R-22 shows it beside the due date, and A-42 ages from it. */
    issuedOn: z.string(),
    dueOn: z.string(),
    state: z.enum(['ISSUED', 'PART_PAID', 'PAID', 'CANCELLED']),
    /** Derived: due date has passed and the balance is not nil. Never stored. */
    overdue: z.boolean(),
  })),
});
export type PayableView = z.infer<typeof zPayableView>;

/** FN-04 / FN-05 — Admin records the accounting-system invoice over sold pieces. */
export const zRecordPurchaseInvoice = z.object({
  reference: z.string().trim().min(1).max(60),
  pieceIds: z.array(zId).min(1).max(500),
  issuedOn: z.string().regex(/^\d{4}-\d{2}-\d{2}$/),
  /** Defaults to the portal's due rule when omitted. */
  dueOn: z.string().regex(/^\d{4}-\d{2}-\d{2}$/).optional(),
  /**
   * FN-17, D76 — the tax amount, **entered and never computed**. Miraki trades internationally
   * from Hong Kong, so the portal holds no rates and no jurisdiction logic; a portal's treatment
   * is a commercial setting whose v1 value is "Other — manual".
   *
   * Optional, and **zero is a valid answer rather than a missing one** — most invoices on this
   * model carry no tax at all. There is no `subtotal` or `total` field beside it: FN-04 computes
   * the subtotal from the covered pieces' Sale entries and refuses a second figure for the same
   * debt, and the total follows from the two.
   */
  tax: z.string().regex(MONEY_PATTERN, 'must be a decimal amount').optional(),
});
export type RecordPurchaseInvoice = z.infer<typeof zRecordPurchaseInvoice>;

/** FN-06 — Admin confirms an offline payment against one or more invoices. */
export const zConfirmSalePayment = z.object({
  amount: z.string().regex(MONEY_PATTERN),
  receivedOn: z.string().regex(/^\d{4}-\d{2}-\d{2}$/),
  bankReference: z.string().trim().max(80).optional(),
  allocations: z.array(z.object({ invoiceId: zId, amount: z.string().regex(MONEY_PATTERN) })).min(1),
});
export type ConfirmSalePayment = z.infer<typeof zConfirmSalePayment>;

/** BG-07 — a budget change always carries a reason. */
export const zSetBudget = z.object({
  amount: z.string().regex(MONEY_PATTERN),
  reason: z.string().trim().min(3).max(300),
  /**
   * BG-04 — this retailer's tolerance band, as a percentage of their available budget.
   *
   * **Three states, all distinct.** Absent leaves whatever the account has alone. `null` clears
   * the override, so the account takes the portal's figure again. A value — including `'0'` —
   * sets it for this account, and `'0'` means no tolerance at all, which is the rule as it stood
   * before the band existed. Collapsing null and zero is the one mistake this field can make.
   *
   * `zPercent`, so the same bound applies here as everywhere else a percentage crosses the wire:
   * a decimal string, four places at most, never above 100 and never a `number` (NF-04).
   */
  tolerancePct: zPercent.nullable().optional(),
});
export type SetBudget = z.infer<typeof zSetBudget>;

/** SL-09 — Admin reverses a sale before it is invoiced. */
export const zReverseSale = z.object({ reason: z.string().trim().min(3).max(500) });
export type ReverseSale = z.infer<typeof zReverseSale>;

// ------------------------------------------------------------------ SL-04a, the declaration cut-off

/**
 * SL-04a — **the deadline by which a month's sales must all be declared.** A CUT-OFF, not a
 * window.
 *
 * Client direction, 30-Oct-2026: *"all goods sold need to be declared on the website by the last
 * date of the month/first date of the new month. let's keep this as a 2-day time frame."*
 *
 * **Nothing here refuses anything, and nothing here is ever stored.** Reporting a sale behaves
 * identically on day 1, day 2 and day 40 — SL-03a, verbatim: *"what waits is the release, not the
 * report."* A portal that refused a late declaration would lose the record the consignment model
 * depends on, and Miraki's own basis for invoicing with it. These functions return a label for a
 * reminder and for an Admin list; no caller may turn one into a gate.
 *
 * The other reading — that reporting is POSSIBLE only on those two days — is arithmetically
 * incompatible with SL-04 as already built: `SALE_DATE_BACK_DAYS` above is 7, enforced with a
 * `TOO_FAR_BACK` refusal, so a retailer who sold on the 3rd could not report it when the window
 * opened on the 30th. Making the window work would mean widening that bound to 31+ days, which
 * turns the sale date from a bounded fact into an unverifiable claim and corrupts everything
 * derived from it — `daysHeld` (D80), days-to-sale (AN-02), best-sellers (SL-08) and AN-13's
 * non-moving cohort. **`SALE_DATE_BACK_DAYS` stays 7.**
 *
 * In this package for the same reason `SALE_DATE_BACK_DAYS` is: the server computes the deadline
 * for the Admin list and the reminder, and the retailer's screen shows it. Two declarations of one
 * date would be two dates. **The review rule for this feature is a grep — a second computation of
 * a month boundary or a declaration deadline anywhere outside this file is the defect.**
 *
 * Every date in and out is a calendar day in the PORTAL's zone (D49); the caller resolves it with
 * `dayIn()`, never the server's clock and never the browser's. The arithmetic runs on UTC
 * midnights, as `invoicing.ts` already requires, so a daylight-saving change cannot move a date by
 * a day — and the month boundary is day 0 of the next month, so there is no leap-year or 31st case.
 */

/**
 * The latitude granted past the month's end: the 1st of the new month is still inside the window,
 * so **the first late day is the 2nd**.
 *
 * The client named the days explicitly — *"the last date of the month/first date of the new
 * month"* — so the window is {last day of M, 1st of M+1} and lateness begins on day 2, not day 3.
 * One integer, and it is Unresolved pending the client's confirmation (feasibility §3.9): awkward
 * to change once a retailer has been told a date, which is why it is one named constant.
 */
export const DECLARATION_GRACE_DAYS = 1;

/**
 * How long a closed month stays worth chasing: through day 7 of the new month.
 *
 * It is what makes Admin's tile disappear rather than sit permanently amber — DB-07's own rule is
 * that a tile a user cannot action is NOT SHOWN, and on the 12th there is nothing to action. Day 7
 * because that is when FN-05's monthly payable falls due on a `MONTH_END` portal; after it the
 * month is Accounts' problem rather than a chase.
 *
 * **Deliberately a constant here and NOT read from the portal's `payableDueRule.graceDays`**, even
 * though GCC's figure happens to be the same 7. The declaration deadline and the payment due date
 * are different dates and are never conflated (the defect D75 was raised to fix): deriving one
 * from the other is exactly that conflation, and it would make Admin's tile change its lifetime
 * whenever Accounts changed a payment term.
 */
export const DECLARATION_CHASE_DAYS = 7;

/** The calendar month whose sales are being declared. `from` is always a 'YYYY-MM-01'. */
export interface DeclarationPeriod { readonly from: string; readonly to: string }

/**
 * Where the month stands against its deadline.
 *
 *   OPEN  the month is still running — the deadline is ahead
 *   DUE   the last day of the month, and the 1st: inside the window, not yet a failure
 *   LATE  from the 2nd — a fact rather than a prompt, and when Accounts needs it
 *
 * **LATE is a label, never a consequence.** The consequence already exists and needs no code:
 * `periodOf()` keys the monthly payable on the INVOICE's issue date, so a sale declared after
 * Admin raised the month's invoice lands in the next month's payable. Lateness already costs
 * Miraki a month of cash-flow timing, which is what makes this a commercial expectation enforced
 * by a conversation rather than a system rule. Nothing may add a penalty, a block, a ledger entry
 * or an audit row for a missed deadline — a missed deadline is the ABSENCE of an event, and
 * FN-01's entry types are a closed list with no member for it.
 */
export type DeclarationState = 'OPEN' | 'DUE' | 'LATE';

const utcDay = (day: string): Date => new Date(`${day}T00:00:00Z`);
const asDay = (d: Date): string => d.toISOString().slice(0, 10);

/** The whole calendar month a day falls in. Day 0 of the next month is the last day of this one. */
function monthOf(day: string): DeclarationPeriod {
  const d = utcDay(day);
  return {
    from: asDay(new Date(Date.UTC(d.getUTCFullYear(), d.getUTCMonth(), 1))),
    to: asDay(new Date(Date.UTC(d.getUTCFullYear(), d.getUTCMonth() + 1, 0))),
  };
}

/**
 * **The month being DECLARED, which is not always the month containing `today`.**
 *
 * On 1 November the month being declared is OCTOBER — its deadline is that very day. Getting this
 * backwards marks every punctual retailer late, and it is the one bug a reader will not see in
 * review, so it is one function with its own tests.
 *
 * It rolls forward once the closed month stops being worth chasing, on day
 * `DECLARATION_CHASE_DAYS + 1`. So: 15-Oct → October (open); 31-Oct → October (due); 1-Nov →
 * October (due); 2-Nov through 7-Nov → October (late); 8-Nov → November (open again).
 */
export function declarationPeriod(today: string): DeclarationPeriod {
  const d = utcDay(today);
  if (d.getUTCDate() <= DECLARATION_CHASE_DAYS) {
    // Day 0 of this month is the last day of the previous one — the month still being chased.
    return monthOf(asDay(new Date(Date.UTC(d.getUTCFullYear(), d.getUTCMonth(), 0))));
  }
  return monthOf(today);
}

/**
 * The date by which the period must be complete: its last day plus the grace day.
 *
 * **Not the payment due date.** On a `MONTH_END` portal they are the 1st and the 7th, six days
 * apart, and the gap between them is Admin's whole window to raise the invoice (FN-05). Showing a
 * retailer one while holding them to the other is the D75 defect in a new place, which is why this
 * is a separate function from `dueDateFor()` and shares no code with it.
 */
export function declarationDeadline(period: DeclarationPeriod): string {
  const d = utcDay(period.to);
  d.setUTCDate(d.getUTCDate() + DECLARATION_GRACE_DAYS);
  return asDay(d);
}

/** Where `period` stands on the portal's `today`. One string comparison; nothing is read. */
export function declarationState(period: DeclarationPeriod, today: string): DeclarationState {
  if (today > declarationDeadline(period)) return 'LATE';
  if (today >= period.to) return 'DUE';
  return 'OPEN';
}

/**
 * Has this account told us about a sale for this period?
 *
 * **Keyed on the REPORT moment (`soldReportedAt`), never on the sale date (`soldOn`).** The
 * question is *"did they tell us during this declaration cycle"*, not *"did a sale fall in the
 * month"*: SL-04 lets `soldOn` sit legitimately in the previous month, and keying on it would mark
 * a punctual retailer late. The two columns exist for exactly this distinction.
 *
 * The test is `>= period.from` rather than "inside the period", and that is the 1-November case: a
 * sale made on 28 October and reported on 1 November — legal, four days back, inside
 * `SALE_DATE_BACK_DAYS` — IS an October declaration, because on 1 November October is the month
 * being declared. A report can never be in the future, so no upper bound is needed.
 *
 * What it CANNOT answer is whether they told us about *all* of them. The portal has no independent
 * knowledge of a retailer's sales, so "partially declared" is not a computable state, and the list
 * built on this is a conversation list and never a compliance list. Closing that gap would need a
 * nil declaration — an affirmative *"no sales in October"* — which is the only part of SL-04a that
 * would need a table, is Unresolved, and is deliberately not built.
 */
export const declaredIn = (period: DeclarationPeriod, lastReportedOn: string | null): boolean =>
  lastReportedOn !== null && lastReportedOn >= period.from;

/**
 * Does SL-04a apply on this portal at all? **Gate on the SETTING, never on a country (D46).**
 *
 * The deadline exists so a calendar month's sales can be billed as one payable (FN-05), which is
 * what `MONTH_END` means. A `NET_DAYS` portal invoices per sale and has no month to close, so a
 * monthly declaration deadline there closes nothing and is a reminder nobody acts on. Unresolved
 * pending the client (feasibility §6.5, Q3); this is Tekfilo's recommendation.
 *
 * ⚠️ **Pass the PORTAL's `payableDueRule`, never the per-account resolved one.** `periodOf()` takes
 * the resolved rule, deliberately and for a good reason of its own — an account with its own
 * net-days term is excluded from the portal's monthly payable. Copy that precedent here and
 * **GenZ Jewels, the first and currently only GCC retailer, which has a 60-day term (D75), gets no
 * declaration deadline at all** — silently, with the feature looking correct because the list is
 * empty. A retailer's own term changes when they must PAY; the declaration rhythm is when Miraki
 * closes its books on a month, and that is a portal-level rhythm. There is a test for this.
 */
export const declarationAppliesTo = (rule: { readonly type: 'NET_DAYS' | 'MONTH_END' }): boolean =>
  rule.type === 'MONTH_END';
